import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Collapse,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Link as MUILink,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import {
  Home,
  KeyboardArrowDown,
  KeyboardArrowRight,
  LocalShipping,
  Search,
  UnfoldLess,
  UnfoldMore,
} from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { type Dayjs } from 'dayjs';
import { useWriteGuard } from '@/hooks/useWriteGuard';
import { TransfersShippingModal } from '@/components/transfers-shipping';
import {
  useTransfersShippingLogs,
  useTransfersShippingLogItems,
} from '@/hooks/queries/transfers-shipping';
import { useAllActiveCarts } from '@/hooks/queries/cart/useCartQueries';
import { useClearCart } from '@/hooks/mutations/cart/useCartMutations';
import type { WorkflowCart } from '@/services/cart';
import { usePageTitle } from '@/hooks/usePageTitle';
import type { TransfersShippingType } from '@/services/transfers-shipping';
import { getErrorMessage } from '@/utils/httpClient';
import { multiWordMatch } from '@/utils/multiWordMatch';

type TypeFilter = 'all' | TransfersShippingType;
type TransferSortField = 'typeLabel' | 'date_sent' | 'total_quantity';

export const TransfersShippingPage: React.FC = () => {
  const navigate = useNavigate();
  usePageTitle('Transfers');
  const isViewer = useWriteGuard();

  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [shipmentModalOpen, setShipmentModalOpen] = useState(false);
  const [cancelCart, setCancelCart] = useState<WorkflowCart | null>(null);
  const [newWorkflowPrompt, setNewWorkflowPrompt] = useState<TransfersShippingType | null>(null);
  const [isResolvingNewWorkflow, setIsResolvingNewWorkflow] = useState(false);

  const logsQuery = useTransfersShippingLogs();
  const logs = useMemo(() => logsQuery.data ?? [], [logsQuery.data]);

  const { data: allActiveCarts = [], isLoading: isCartsLoading } = useAllActiveCarts();
  const clearCartMutation = useClearCart();

  const savedTransferCarts = useMemo(
    () => allActiveCarts.filter(
      (c) => (c.workflow_type === 'transfer' || c.workflow_type === 'shipment') && (c.item_count ?? 0) > 0,
    ),
    [allActiveCarts],
  );

  const savedTransferCartForUser = useMemo(
    () => savedTransferCarts.find((cart) => cart.workflow_type === 'transfer') ?? null,
    [savedTransferCarts],
  );

  const savedShipmentCartForUser = useMemo(
    () => savedTransferCarts.find((cart) => cart.workflow_type === 'shipment') ?? null,
    [savedTransferCarts],
  );

  const openWorkflowModal = useCallback((workflowType: TransfersShippingType) => {
    if (workflowType === 'transfer') {
      setTransferModalOpen(true);
      return;
    }
    setShipmentModalOpen(true);
  }, []);

  const handleOpenNewWorkflow = useCallback(
    (workflowType: TransfersShippingType) => {
      const existingCart =
        workflowType === 'transfer' ? savedTransferCartForUser : savedShipmentCartForUser;

      if (existingCart) {
        setNewWorkflowPrompt(workflowType);
        return;
      }

      openWorkflowModal(workflowType);
    },
    [savedTransferCartForUser, savedShipmentCartForUser, openWorkflowModal],
  );

  const handleStartNewWorkflow = useCallback(async () => {
    if (!newWorkflowPrompt || isResolvingNewWorkflow) {
      return;
    }

    const existingCart =
      newWorkflowPrompt === 'transfer' ? savedTransferCartForUser : savedShipmentCartForUser;

    if (!existingCart?.customer_id) {
      return;
    }

    setIsResolvingNewWorkflow(true);
    try {
      const result = await clearCartMutation.mutateAsync({
        customerId: existingCart.customer_id,
        cartId: existingCart.id,
        workflowType: newWorkflowPrompt,
      });

      if (result.success) {
        setNewWorkflowPrompt(null);
        openWorkflowModal(newWorkflowPrompt);
      }
    } catch {
      // Errors handled by mutation notifications
    } finally {
      setIsResolvingNewWorkflow(false);
    }
  }, [
    newWorkflowPrompt,
    isResolvingNewWorkflow,
    savedTransferCartForUser,
    savedShipmentCartForUser,
    clearCartMutation,
    openWorkflowModal,
  ]);

  const handleCancelSavedCart = useCallback(async () => {
    if (!cancelCart?.customer_id) return;
    try {
      const result = await clearCartMutation.mutateAsync({
        customerId: cancelCart.customer_id,
        cartId: cancelCart.id,
      });
      if (result.success) {
        setCancelCart(null);
      }
    } catch {
      // Errors handled by mutation notifications
    }
  }, [cancelCart, clearCartMutation]);

  const [searchText, setSearchText] = useState('');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(null);
  const [dateTo, setDateTo] = useState<Dayjs | null>(null);
  const dateRangeInvalid = Boolean(dateFrom?.isValid() && dateTo?.isValid() && dateFrom.isAfter(dateTo, 'day'));
  const [sortField, setSortField] = useState<TransferSortField | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const mappedLogs = useMemo(() => {
    return logs.map((log) => ({
      ...log,
      typeLabel:
        log.transfer_type === 'transfer'
          ? 'Transfer'
          : log.transfer_type === 'shipment'
            ? 'Shipment'
            : '—',
    }));
  }, [logs]);

  const filteredLogs = useMemo(() => {
    let result = mappedLogs;

    const query = searchText.trim().toLowerCase();
    if (query) {
      result = result.filter((log) => {
        const parts = [log.display_name ?? '', log.entity_identifier ?? '', log.typeLabel];
        return multiWordMatch(parts.join(' '), query);
      });
    }

    if (typeFilter !== 'all') {
      result = result.filter((log) => log.transfer_type === typeFilter);
    }

    if (dateRangeInvalid) return [];

    const from = dateFrom ? dateFrom.startOf('day').valueOf() : null;
    const to = dateTo ? dateTo.endOf('day').valueOf() : null;

    if (from !== null || to !== null) {
      result = result.filter((log) => {
        const sentAt = dayjs(log.date_sent);
        if (!sentAt.isValid()) {
          return false;
        }
        const sentAtValue = sentAt.valueOf();
        if (from !== null && sentAtValue < from) {
          return false;
        }
        if (to !== null && sentAtValue > to) {
          return false;
        }
        return true;
      });
    }

    return result;
  }, [mappedLogs, searchText, typeFilter, dateFrom, dateTo, dateRangeInvalid]);

  const sortedLogs = useMemo(() => {
    if (!sortField) return filteredLogs;
    return [...filteredLogs].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;
      switch (sortField) {
        case 'typeLabel':
          aVal = a.typeLabel.toLowerCase();
          bVal = b.typeLabel.toLowerCase();
          break;
        case 'date_sent':
          aVal = a.date_sent ?? '';
          bVal = b.date_sent ?? '';
          break;
        case 'total_quantity':
          aVal = a.total_quantity ?? 0;
          bVal = b.total_quantity ?? 0;
          break;
        default:
          return 0;
      }
      if (aVal === bVal) return 0;
      const result = aVal < bVal ? -1 : 1;
      return sortDirection === 'asc' ? result : -result;
    });
  }, [filteredLogs, sortField, sortDirection]);

  const handleSort = useCallback((field: TransferSortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  }, [sortField]);

  const logIds = useMemo(() => sortedLogs.map((log) => log.id), [sortedLogs]);
  const { itemsByLogId, isLoading: isLoadingLogItems } = useTransfersShippingLogItems(logIds);

  const [expandedLogIds, setExpandedLogIds] = useState<Set<string>>(() => new Set());
  const toggleLogExpansion = useCallback((logId: string) => {
    setExpandedLogIds((prev) => {
      const next = new Set(prev);
      if (next.has(logId)) {
        next.delete(logId);
      } else {
        next.add(logId);
      }
      return next;
    });
  }, []);

  const handleExpandAll = useCallback(() => {
    setExpandedLogIds(new Set(sortedLogs.map((log) => log.id)));
  }, [sortedLogs]);

  const handleCollapseAll = useCallback(() => {
    setExpandedLogIds(new Set());
  }, []);

  const allExpanded = expandedLogIds.size === sortedLogs.length && sortedLogs.length > 0;
  const noneExpanded = expandedLogIds.size === 0;

  return (
    <Container maxWidth={false} sx={{ mt: 3, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <MUILink
            color="inherit"
            href="/"
            onClick={(event) => {
              event.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </MUILink>
          <Typography color="text.primary">Transfers</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <LocalShipping color="primary" sx={{ fontSize: 36 }} />
          <Typography variant="h4" component="h1">
            Transfers
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1.5 }}>
          Create lateral transfers or shipments for in-stock assets and track completed activity.
        </Typography>
      </Box>

      <Paper elevation={1} sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                size="small"
                label="Search"
                placeholder="Search logs..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 200 }}
              />

              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="transfers-shipping-type-filter-label">Type</InputLabel>
                <Select
                  labelId="transfers-shipping-type-filter-label"
                  id="transfers-shipping-type-filter"
                  value={typeFilter}
                  label="Type"
                  onChange={(event) => setTypeFilter(event.target.value as TypeFilter)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="transfer">Transfer</MenuItem>
                  <MenuItem value="shipment">Shipment</MenuItem>
                </Select>
              </FormControl>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Date Sent From"
                  value={dateFrom}
                  onChange={(value) => setDateFrom(value?.isValid() ? value : null)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { minWidth: 180 },
                    },
                  }}
                />

                <DatePicker
                  label="Date Sent To"
                  value={dateTo}
                  onChange={(value) => setDateTo(value?.isValid() ? value : null)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { minWidth: 180 },
                    },
                  }}
                />
              </LocalizationProvider>

              {dateRangeInvalid && (
                <Alert severity="error">Date From cannot be after Date To.</Alert>
              )}

              {filteredLogs.length > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={allExpanded ? <UnfoldLess /> : <UnfoldMore />}
                  onClick={allExpanded ? handleCollapseAll : handleExpandAll}
                  disabled={sortedLogs.length === 0}
                >
                  {allExpanded ? 'Collapse All' : noneExpanded ? 'Expand All' : 'Expand All'}
                </Button>
              )}
            </Box>

            {!isViewer && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                disabled={isCartsLoading}
                onClick={() => handleOpenNewWorkflow('transfer')}
              >
                New 3161
              </Button>
              <Button
                variant="contained"
                color="secondary"
                disabled={isCartsLoading}
                onClick={() => handleOpenNewWorkflow('shipment')}
              >
                New 1149
              </Button>
            </Box>
            )}
          </Box>

          {savedTransferCarts.length > 0 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>Saved Transfer Carts</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small" aria-label="Saved transfer carts">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Items</TableCell>
                      <TableCell>Date Created</TableCell>
                      <TableCell>Last Updated</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {savedTransferCarts.map((cart) => (
                      <TableRow key={cart.id} hover>
                        <TableCell>
                          <Chip
                            size="small"
                            label={cart.workflow_type === 'transfer' ? 'Transfer' : 'Shipment'}
                            color={cart.workflow_type === 'transfer' ? 'info' : 'success'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">{cart.item_count ?? 0}</TableCell>
                        <TableCell>
                          {dayjs(cart.created_at).isValid()
                            ? dayjs(cart.created_at).format('MM/DD/YYYY HH:mm')
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {dayjs(cart.updated_at).isValid()
                            ? dayjs(cart.updated_at).format('MM/DD/YYYY HH:mm')
                            : '—'}
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            {!isViewer && (
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => {
                                  if (cart.workflow_type === 'transfer') {
                                    setTransferModalOpen(true);
                                  } else {
                                    setShipmentModalOpen(true);
                                  }
                                }}
                              >
                                Resume
                              </Button>
                            )}
                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              disabled={clearCartMutation.isPending}
                              onClick={() => setCancelCart(cart)}
                            >
                              Cancel
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {logsQuery.isError ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => void logsQuery.refetch()}>
                  Retry
                </Button>
              }
            >
              {getErrorMessage(logsQuery.error) || 'Failed to load transfers and shipments.'}
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="medium" aria-label="Transfers">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 48 }} />
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'typeLabel'}
                        direction={sortField === 'typeLabel' ? sortDirection : 'asc'}
                        onClick={() => handleSort('typeLabel')}
                      >
                        Type
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'date_sent'}
                        direction={sortField === 'date_sent' ? sortDirection : 'asc'}
                        onClick={() => handleSort('date_sent')}
                      >
                        Date Sent
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortField === 'total_quantity'}
                        direction={sortField === 'total_quantity' ? sortDirection : 'asc'}
                        onClick={() => handleSort('total_quantity')}
                        sx={{ flexDirection: 'row' }}
                      >
                        Total Qty
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logsQuery.isLoading ? (
                    <TableRow>
                      <TableCell colSpan={4}>Loading...</TableCell>
                    </TableRow>
                  ) : sortedLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4}>No transfers or shipments found</TableCell>
                    </TableRow>
                  ) : (
                    sortedLogs.map((log) => {
                      const isExpanded = expandedLogIds.has(log.id);
                      const items = itemsByLogId.get(log.id) ?? [];
                      const hasLineItems = (log.total_quantity ?? 0) > 0;
                      const typeColor =
                        log.transfer_type === 'transfer'
                          ? 'info'
                          : log.transfer_type === 'shipment'
                            ? 'success'
                            : 'default';

                      return (
                        <React.Fragment key={log.id}>
                          <TableRow
                            hover
                            sx={{
                              '& > *': {
                                borderBottom: isExpanded && hasLineItems ? 'unset' : undefined,
                              },
                              cursor: hasLineItems ? 'pointer' : 'default',
                            }}
                            onClick={() => hasLineItems && toggleLogExpansion(log.id)}
                          >
                            <TableCell sx={{ width: 48 }}>
                              {hasLineItems && (
                                <IconButton
                                  size="small"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    toggleLogExpansion(log.id);
                                  }}
                                  aria-label={isExpanded ? 'Collapse log' : 'Expand log'}
                                >
                                  {isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                                </IconButton>
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={log.typeLabel}
                                color={typeColor}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              {dayjs(log.date_sent).isValid()
                                ? dayjs(log.date_sent).format('MM/DD/YYYY')
                                : '—'}
                            </TableCell>
                            <TableCell align="right">{log.total_quantity ?? '—'}</TableCell>
                          </TableRow>

                          {hasLineItems && (
                            <TableRow>
                              <TableCell colSpan={4} sx={{ py: 0, bgcolor: 'action.hover' }}>
                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                  <Box sx={{ py: 2, px: 4 }}>
                                    <Typography
                                      variant="subtitle2"
                                      gutterBottom
                                      component="div"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      Line Items
                                    </Typography>
                                    <Table
                                      size="small"
                                      aria-label="Transfer or shipment line items"
                                    >
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Nomenclature</TableCell>
                                          <TableCell>LIN</TableCell>
                                          <TableCell>NSN</TableCell>
                                          <TableCell align="right">Qty Sent</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {isLoadingLogItems ? (
                                          <TableRow>
                                            <TableCell colSpan={4}>Loading...</TableCell>
                                          </TableRow>
                                        ) : items.length === 0 ? (
                                          <TableRow>
                                            <TableCell colSpan={4}>No line items found</TableCell>
                                          </TableRow>
                                        ) : (
                                          items.map((item) => (
                                            <TableRow key={item.id}>
                                              <TableCell>
                                                {item.nomenclature ?? item.asset_name ?? '—'}
                                              </TableCell>
                                              <TableCell>{item.lin ?? '—'}</TableCell>
                                              <TableCell>{item.nsn ?? '—'}</TableCell>
                                              <TableCell align="right">
                                                {item.sent_quantity ?? 0}
                                              </TableCell>
                                            </TableRow>
                                          ))
                                        )}
                                      </TableBody>
                                    </Table>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      </Paper>

      <Dialog
        open={newWorkflowPrompt !== null}
        onClose={() => !isResolvingNewWorkflow && setNewWorkflowPrompt(null)}
        aria-labelledby="new-transfer-workflow-title"
      >
        <DialogTitle id="new-transfer-workflow-title">Existing Saved Cart Found</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You already have a saved {newWorkflowPrompt === 'shipment' ? 'shipment' : 'transfer'}
            {' '}cart. Resume it to continue where you left off, or cancel it to start a brand new
            {' '}{newWorkflowPrompt === 'shipment' ? 'shipment' : 'transfer'} cart.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setNewWorkflowPrompt(null)}
            color="inherit"
            disabled={isResolvingNewWorkflow}
          >
            Back
          </Button>
          <Button
            onClick={() => {
              const workflowType = newWorkflowPrompt;
              setNewWorkflowPrompt(null);
              if (workflowType) {
                openWorkflowModal(workflowType);
              }
            }}
            variant="outlined"
            disabled={isResolvingNewWorkflow}
          >
            Resume Saved Cart
          </Button>
          <Button
            onClick={() => void handleStartNewWorkflow()}
            color="error"
            variant="contained"
            disabled={isResolvingNewWorkflow}
          >
            {isResolvingNewWorkflow ? 'Starting New…' : 'Start New Cart'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cancelCart !== null}
        onClose={() => !clearCartMutation.isPending && setCancelCart(null)}
        aria-labelledby="cancel-transfer-cart-title"
      >
        <DialogTitle id="cancel-transfer-cart-title">Cancel Saved Cart</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will release all reserved items in this {cancelCart?.workflow_type === 'shipment' ? 'shipment' : 'transfer'} cart. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelCart(null)} disabled={clearCartMutation.isPending}>
            Keep Cart
          </Button>
          <Button
            onClick={() => void handleCancelSavedCart()}
            color="error"
            variant="contained"
            disabled={clearCartMutation.isPending}
          >
            Cancel Cart
          </Button>
        </DialogActions>
      </Dialog>

      <TransfersShippingModal
        open={transferModalOpen}
        onClose={() => setTransferModalOpen(false)}
        transferType="transfer"
      />
      <TransfersShippingModal
        open={shipmentModalOpen}
        onClose={() => setShipmentModalOpen(false)}
        transferType="shipment"
      />
    </Container>
  );
};
